import { useState, useEffect, useMemo } from 'react';

interface PricePoint {
  timestamp: number;
  price: number;
}

interface Props {
  tokenAddress: string;
  height?: number;
}

type TimeRange = '1h' | '24h' | '7d';

// Generate mock price data (replace with real API call)
function generateMockPriceData(range: TimeRange): PricePoint[] {
  const now = Date.now();
  const points: PricePoint[] = [];

  let interval: number;
  let numPoints: number;

  switch (range) {
    case '1h':
      interval = 60 * 1000; // 1 minute
      numPoints = 60;
      break;
    case '24h':
      interval = 15 * 60 * 1000; // 15 minutes
      numPoints = 96;
      break;
    case '7d':
      interval = 2 * 60 * 60 * 1000; // 2 hours
      numPoints = 84;
      break;
  }

  let basePrice = 0.0001 + Math.random() * 0.0009;

  for (let i = numPoints - 1; i >= 0; i--) {
    // Random walk with trend
    const change = (Math.random() - 0.48) * 0.05;
    basePrice = Math.max(0.00001, basePrice * (1 + change));

    points.push({
      timestamp: now - i * interval,
      price: basePrice,
    });
  }

  return points;
}

export function PriceChart({ tokenAddress, height = 120 }: Props) {
  const [range, setRange] = useState<TimeRange>('24h');
  const [data, setData] = useState<PricePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setData(generateMockPriceData(range));
      setLoading(false);
    }, 300);
  }, [tokenAddress, range]);

  const { minPrice, maxPrice, priceChange, chartPath, areaPath } = useMemo(() => {
    if (data.length === 0) {
      return { minPrice: 0, maxPrice: 0, priceChange: 0, chartPath: '', areaPath: '' };
    }

    const prices = data.map((d) => d.price);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const padding = (max - min) * 0.1 || 0.00001;

    const adjustedMin = min - padding;
    const adjustedMax = max + padding;

    const firstPrice = data[0].price;
    const lastPrice = data[data.length - 1].price;
    const change = ((lastPrice - firstPrice) / firstPrice) * 100;

    const width = 100;
    const chartHeight = height - 20;

    const points = data.map((d, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = chartHeight - ((d.price - adjustedMin) / (adjustedMax - adjustedMin)) * chartHeight;
      return `${x},${y}`;
    });

    const linePath = `M ${points.join(' L ')}`;
    const area = `${linePath} L ${width},${chartHeight} L 0,${chartHeight} Z`;

    return {
      minPrice: min,
      maxPrice: max,
      priceChange: change,
      chartPath: linePath,
      areaPath: area,
    };
  }, [data, height]);

  const isPositive = priceChange >= 0;
  const color = isPositive ? 'var(--success)' : 'var(--error)';

  if (loading) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div
          className="animate-spin"
          style={{
            width: '20px',
            height: '20px',
            border: '2px solid var(--border)',
            borderTopColor: 'var(--accent)',
            borderRadius: '50%',
          }}
        />
      </div>
    );
  }

  const hoveredPoint = hoveredIndex !== null ? data[hoveredIndex] : null;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div>
          <span style={{
            fontSize: '13px',
            fontWeight: 600,
            color: color,
          }}>
            {isPositive ? '+' : ''}{priceChange.toFixed(2)}%
          </span>
          <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginLeft: '8px' }}>
            {range === '1h' ? '1 Hour' : range === '24h' ? '24 Hours' : '7 Days'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '4px' }}>
          {(['1h', '24h', '7d'] as TimeRange[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              style={{
                padding: '4px 8px',
                fontSize: '11px',
                fontWeight: 600,
                color: range === r ? 'var(--text-primary)' : 'var(--text-tertiary)',
                background: range === r ? 'var(--surface-secondary)' : 'transparent',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
              }}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div
        style={{ position: 'relative', height: height - 20 }}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 100 ${height - 20}`}
          preserveAspectRatio="none"
          style={{ overflow: 'visible' }}
        >
          {/* Gradient fill */}
          <defs>
            <linearGradient id={`gradient-${tokenAddress}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={color} stopOpacity="0.3" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Area fill */}
          <path
            d={areaPath}
            fill={`url(#gradient-${tokenAddress})`}
          />

          {/* Line */}
          <path
            d={chartPath}
            fill="none"
            stroke={color}
            strokeWidth="1.5"
            vectorEffect="non-scaling-stroke"
          />

          {/* Hover detection areas */}
          {data.map((_, i) => (
            <rect
              key={i}
              x={(i / (data.length - 1)) * 100 - 0.5}
              y="0"
              width="1"
              height={height - 20}
              fill="transparent"
              onMouseEnter={() => setHoveredIndex(i)}
              style={{ cursor: 'crosshair' }}
            />
          ))}

          {/* Hover indicator */}
          {hoveredIndex !== null && (
            <>
              <line
                x1={(hoveredIndex / (data.length - 1)) * 100}
                y1="0"
                x2={(hoveredIndex / (data.length - 1)) * 100}
                y2={height - 20}
                stroke="var(--text-tertiary)"
                strokeWidth="1"
                strokeDasharray="2,2"
                vectorEffect="non-scaling-stroke"
              />
              <circle
                cx={(hoveredIndex / (data.length - 1)) * 100}
                cy={
                  (height - 20) -
                  ((data[hoveredIndex].price - (minPrice - (maxPrice - minPrice) * 0.1)) /
                    ((maxPrice + (maxPrice - minPrice) * 0.1) - (minPrice - (maxPrice - minPrice) * 0.1))) *
                    (height - 20)
                }
                r="3"
                fill={color}
                stroke="var(--surface)"
                strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
              />
            </>
          )}
        </svg>

        {/* Tooltip */}
        {hoveredPoint && (
          <div
            style={{
              position: 'absolute',
              left: `${(hoveredIndex! / (data.length - 1)) * 100}%`,
              top: '0',
              transform: 'translateX(-50%)',
              padding: '4px 8px',
              background: 'var(--surface-secondary)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '11px',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
          >
            <div style={{ fontWeight: 600 }}>${hoveredPoint.price.toFixed(6)}</div>
            <div style={{ color: 'var(--text-tertiary)' }}>
              {new Date(hoveredPoint.timestamp).toLocaleTimeString()}
            </div>
          </div>
        )}
      </div>

      {/* Price range */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '8px',
        fontSize: '10px',
        color: 'var(--text-tertiary)',
      }}>
        <span>L: ${minPrice.toFixed(6)}</span>
        <span>H: ${maxPrice.toFixed(6)}</span>
      </div>
    </div>
  );
}
